import { barAvatarPlugin } from '../../subpages/ConnectionsUsagesPage/components/ProvidersUsagesChart/bar-avatar-plugin'
import { BarChart } from '../../../../components/Charts/BarChart'
import { BigNumber, Button } from '../../../../components'
import { BoxChart } from '../../../../components/Charts/BoxChart'
import { endOfMonth, startOfMonth, subMonths } from 'date-fns'
import { formatCurrency } from '../../subpages/ConnectionsUsagesPage/format-currency'
import { formatTasks } from '../../subpages/ConnectionsUsagesPage/format-tasks'
import { GetProvidersUsagesQuery } from '@indigohive/cogfy-types/endpoints/getProvidersUsages'
import { MAP_COLOR_BY_PROVIDER } from '../../../../components/__common/map-color-by-provider'
import { MAP_LOGO_BY_PROVIDER } from '../../../../components/__common/map-logo-by-provider'
import { ProviderName, providers } from '@indigohive/cogfy-providers'
import { TooltipItem } from 'chart.js'
import { useCogfy } from '../../../../hooks'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

export function ConnectionsSection () {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const cogfy = useCogfy()

  const {
    data: mostExpensiveConnectionLastMonth,
    isLoading: isLoadingMostExpensiveConnectionLastMonth,
    isError: isErrorMostExpensiveConnectionLastMonth,
    refetch: refetchMostExpensiveConnectionLastMonth
  } = useQuery({
    queryKey: ['getMostExpensiveConnectionLastMonth'],
    queryFn: ({ signal }) => cogfy.getMostExpensiveConnection({
      startDate: startOfMonth(subMonths(new Date(), 1)),
      endDate: endOfMonth(subMonths(new Date(), 1))
    }, { signal })
  })

  const costLastMonth = mostExpensiveConnectionLastMonth
    ? formatCurrency(mostExpensiveConnectionLastMonth.currency as 'USD' | 'BRL' | 'EUR', mostExpensiveConnectionLastMonth.totalCosts)
    : 0

  const getProvidersUsagesQuery: GetProvidersUsagesQuery = {
    startDate: startOfMonth(new Date()).toISOString(),
    endDate: endOfMonth(new Date()).toISOString(),
    connectionOwner: ['record', 'customer'],
    size: 3
  }
  const {
    data: providerUsages,
    isLoading: isLoadingProviderUsages,
    isError: isErrorProviderUsages
  } = useQuery({
    queryKey: ['getProvidersUsages', getProvidersUsagesQuery] as const,
    queryFn: ({ signal }) => cogfy.getProvidersUsages(getProvidersUsagesQuery, { signal })
  })

  const getProvidersUsagesData = {
    labels: providerUsages?.map(p => p.provider) ?? [],
    providerNames: providerUsages?.map(p => p.provider),
    datasets: [
      {
        label: '',
        data: providerUsages?.map(usage => usage.connectionOwner === 'cogfy' ? usage.credits : usage.costs) ?? [],
        tasks: providerUsages?.map(usage => usage.tasks),
        currencies: providerUsages?.map(usage => usage.currency),
        backgroundColor: providerUsages?.map(usage => MAP_COLOR_BY_PROVIDER[usage.provider as ProviderName]).filter(color => color !== undefined) ?? '#000000'
      }
    ]
  }

  return (
    <div className='flex flex-col gap-3'>
      <div className='flex w-full justify-between'>
        <h2 className="text-2xl font-semibold">{t('Connections')}</h2>
        <Button
          className='w-fit bg-transparent h-auto px-3 border-primary text-primary hover:bg-primary hover:text-white'
          onClick={() => navigate('../connections')}
        >
          {t('Details')}
        </Button>
      </div>
      <div className="flex gap-4">
        <BigNumber.Card
          className='flex-1'
          loading={isLoadingMostExpensiveConnectionLastMonth}
          error={isErrorMostExpensiveConnectionLastMonth}
          onRefetchClick={() => { refetchMostExpensiveConnectionLastMonth() }}
        >
          {t('workspaceUsagePage:Highest cost') + ' - ' + t('filters:last month')}
          <span className="text-lg font-semibold">{mostExpensiveConnectionLastMonth?.connectionName}</span>
          <BigNumber.Number>
            <div className="flex items-center gap-2">
              {MAP_LOGO_BY_PROVIDER[mostExpensiveConnectionLastMonth?.provider as ProviderName] && (
              <img
                src={MAP_LOGO_BY_PROVIDER[mostExpensiveConnectionLastMonth?.provider as ProviderName]}
                alt={`${mostExpensiveConnectionLastMonth?.provider}-logo`}
                className="size-9 rounded-md"
              />
              )}
              {costLastMonth || '-'}
            </div>
          </BigNumber.Number>
        </BigNumber.Card>

        <BoxChart
          title={t('workspaceUsagePage:Usage by providers this month')}
          className="flex-1 basis-1/4 max-h-64"
        >
          <BarChart
            data={getProvidersUsagesData}
            options={{
              maintainAspectRatio: false,
              responsive: true,
              layout: {
                padding: {
                  top: 40
                }
              },
              plugins: {
                legend: { display: false },
                tooltip: {
                  callbacks: {
                    title: (tooltip) => (providers.find(provider => provider.name === tooltip.at(0)?.label))?.label,
                    label: (tooltip) => {
                      const label = ' ' + t('workspaceUsagePage:Costs') + '      '
                      return label + formatCurrency(getProvidersUsagesData.datasets[0].currencies?.[tooltip.dataIndex] as 'USD' | 'BRL' | 'EUR' ?? 'USD', tooltip.parsed.y)
                    },
                    afterLabel: (tooltip: TooltipItem<'bar'> & { dataset: { tasks: number [] } }) => ' Tasks: ' + formatTasks(tooltip.dataset.tasks[tooltip.dataIndex], 'standard')
                  }
                }
              },
              scales: {
                x: {
                  stacked: true,
                  grid: { color: 'transparent' },
                  ticks: {
                    callback: (val) => providers.find(provider => provider.name === getProvidersUsagesData.labels[val as number])?.label ?? ''
                  }
                },
                y: { stacked: true }
              }
            }}
            loading={isLoadingProviderUsages}
            error={isErrorProviderUsages}
            plugins={[barAvatarPlugin]}
          />
        </BoxChart>
      </div>
    </div>
  )
}
